import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';
import Loadable from 'react-loadable';
import { useDispatch } from 'react-redux';

import Button from '@atlaskit/button';
import InfoIcon from '@atlaskit/icon/glyph/info';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { HiddenLoader } from 'src/components/react-loadable';
import { useIntl } from 'src/hooks/intl';
import { openSyncBranchDialog } from 'src/redux/branches';
import { BranchSyncInfo as BranchSyncInfoType } from 'src/redux/repo-commits/types';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import branchMessages from 'src/sections/repository/sections/branches/components/branch-table/branch-actions.i18n';
import { getBranchesFullNames } from 'src/utils/compare-branches';

import messages from './branch-sync-info.i18n';
import * as styles from './branch-sync-info.style';

const ConflictsDialog = Loadable({
  loading: HiddenLoader,
  loader: () =>
    import(
      /* webpackChunkName: "pr-commit-list-conflicts-dialog" */ './conflicts-dialog'
    ),
});

export type BranchSyncInfoProps = {
  branchSyncInfo: BranchSyncInfoType | null;
  destinationHash: string;
  sourceHash: string;
  destinationBranchName: string;
  sourceBranchName: string;
  destinationRepositoryFullName: string;
  sourceRepositoryFullName: string;
  appearance?: 'block' | 'inline' | 'section-message';
  hasConflicts: boolean;
  sourceCloneLink: string;
  openSyncDialog: typeof openSyncBranchDialog;
  reloadAction?: { type: string };
  shouldDisableSync?: boolean;
  hideBranchSyncButton?: boolean;
  // context is used to determine if the branch sync info is being shown in the context of a
  // PR or a branch (default) and is used to determine the section message to display
  context?: 'pr' | 'branch';
};

const RepositoryBranchSyncInfo = ({
  branchSyncInfo,
  sourceHash,
  destinationHash,
  destinationBranchName,
  sourceBranchName,
  destinationRepositoryFullName,
  sourceRepositoryFullName,
  appearance = 'block',
  hasConflicts,
  sourceCloneLink,
  openSyncDialog,
  reloadAction,
  shouldDisableSync = false,
  hideBranchSyncButton = false,
  context = 'branch',
}: BranchSyncInfoProps): JSX.Element | null => {
  const [isOpen, setIsConflictsDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();

  // @ts-ignore TODO: fix noImplicitAny error here
  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (hasConflicts) {
      setIsConflictsDialogOpen(true);
      return;
    }

    dispatch(
      openSyncDialog({
        destinationBranchName,
        sourceBranchName,
        destinationRepositoryFullName,
        sourceRepositoryFullName,
        reloadAction,
      })
    );
  };

  const fullNames = getBranchesFullNames(
    sourceBranchName,
    destinationBranchName,
    sourceRepositoryFullName,
    destinationRepositoryFullName
  );

  const Container =
    appearance === 'inline'
      ? styles.BranchSyncInlineContainer
      : styles.BranchSyncBlockContainer;

  const testId = `branch-sync-info-${appearance}`;

  const renderSyncButton = () => {
    const SyncButton = () => {
      const ButtonLink = () => (
        <Button
          appearance="link"
          spacing={appearance === 'section-message' ? 'none' : 'compact'}
          onClick={handleClick}
          isDisabled={shouldDisableSync}
        >
          <FormattedMessage {...messages.action} />
        </Button>
      );

      // don't add additional styling for section message button
      return appearance === 'section-message' ? (
        <ButtonLink />
      ) : (
        <styles.ActionWrapper>
          <ButtonLink />
        </styles.ActionWrapper>
      );
    };

    return (
      <RepositoryAccessGuard requiredLevel="write">
        {!shouldDisableSync ? (
          <SyncButton />
        ) : (
          <Tooltip
            position="top"
            content={intl.formatMessage(branchMessages.syncDisabled, {
              source: sourceBranchName,
            })}
          >
            <span>
              <SyncButton />
            </span>
          </Tooltip>
        )}
      </RepositoryAccessGuard>
    );
  };

  if (branchSyncInfo && branchSyncInfo.behind > 0) {
    return (
      <>
        {appearance === 'section-message' ? (
          <SectionMessage testId={testId}>
            <FormattedMessage
              {...messages.sectionMsgText}
              values={{
                context,
                count: branchSyncInfo.behind,
                countTruncated: branchSyncInfo.behindTruncated,
                sourceBranch: fullNames.sourceBranchName,
              }}
            />{' '}
            {!hideBranchSyncButton ? renderSyncButton() : null}
          </SectionMessage>
        ) : (
          <Container data-testid={testId}>
            {appearance === 'inline' && (
              <styles.InfoIconWrapper data-testid="info-icon">
                <InfoIcon
                  label="info"
                  size="small"
                  primaryColor={token('color.icon.information', colors.B400)}
                />
              </styles.InfoIconWrapper>
            )}
            <span>
              <strong>
                {branchSyncInfo.behind}
                {branchSyncInfo.behindTruncated && '+'}{' '}
              </strong>
              <FormattedMessage
                {...messages.text}
                values={{
                  numberOfCommits: branchSyncInfo.behind,
                  branchName: fullNames.sourceBranchName,
                }}
              />
            </span>
            {!hideBranchSyncButton ? renderSyncButton() : null}
          </Container>
        )}
        {/* include ConflictsDialog for both block and inline appearance */}
        {isOpen && (
          <ConflictsDialog
            sourceCloneLink={sourceCloneLink}
            sourceBranchName={sourceBranchName}
            destinationBranchName={destinationBranchName}
            sourceRepositoryFullName={sourceRepositoryFullName}
            destinationRepositoryFullName={destinationRepositoryFullName}
            sourceHash={sourceHash}
            destinationHash={destinationHash}
            onClose={() => setIsConflictsDialogOpen(false)}
          />
        )}
      </>
    );
  }
  return null;
};

export default RepositoryBranchSyncInfo;
